import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import regression from 'regression';
import type { ApexOptions } from 'apexcharts';

import { LIMIT_METRIC_VOLATILITY, REAL_TIME_SCENARIO, selectors } from 'state';
import { metricInputSettings } from '../TrainerSession/SessionSettings/MetricsLimits2';

type DataPoint = {
  x: number | string;
  y: number;
};

type Props = {
  data: DataPoint[];
  height: number;
  width: number;
  metricInfoDict: { name: string; limit_index: number };
  show_linear_regression?: boolean;
};

type MetricLimits = {
  metric_type: number;
  upper_limit: number | null;
  lower_limit: number | null;
  enforced: boolean;
};

type Zone = {
  value?: number | null;
  color: string;
};

type XY = { x: number; y: number };

type ApexSeries = {
  name: string;
  data: XY[];
  // carry the intended color for THIS segment, so Apex paints it correctly
  _color?: string;
};

const BLUE = '#008efa';
const RED = '#e53935'; // clearer “example-like” red than plain "red"
const REGRESSION_COLOR = 'rgb(67,67,67)';
const ONE_DAY = 86_400_000;

function formatDate(ms: number, mode: 'axis' | 'tooltip') {
  const d = new Date(ms);
  const monthShort = d.toLocaleString('en-US', { month: 'short' });
  const monthLong = d.toLocaleString('en-US', { month: 'long' });
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  if (mode === 'axis') return `${monthShort} ${day}`;
  return `${monthLong} ${day}, ${hh}:${mm}`;
}

// Clean axis labels like your example (no 1.712784438... garbage)
function formatAxisNumber(val: number, maxFrac = 2) {
  if (!Number.isFinite(val)) return '';
  // Avoid "-0"
  const v = Math.abs(val) < 1e-12 ? 0 : val;

  // Show integers as integers
  const isInt = Math.abs(v - Math.round(v)) < 1e-10;
  if (isInt) return String(Math.round(v));

  // Otherwise show up to maxFrac decimals, trimmed
  return v.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac,
  });
}

function computeZones(selected: MetricLimits | undefined): Zone[] {
  if (!selected) return [{ color: BLUE }];

  const hasOnlyUpper =
    selected.upper_limit !== null && selected.lower_limit === null;
  const hasOnlyLower =
    selected.upper_limit === null && selected.lower_limit !== null;

  // Same semantics as Highcharts zones
  let zones: Zone[] = [
    { value: selected.lower_limit, color: RED },
    { value: selected.upper_limit, color: BLUE },
    { color: RED },
  ];

  if (hasOnlyUpper)
    zones = [{ value: selected.upper_limit, color: BLUE }, { color: RED }];
  if (hasOnlyLower)
    zones = [{ value: selected.lower_limit, color: RED }, { color: BLUE }];

  if (selected.enforced === false) zones = [{ color: BLUE }];

  return zones;
}

function zoneColorForY(y: number, zones: Zone[]) {
  for (const z of zones) {
    if (z.value === null || z.value === undefined) continue;
    if (y <= z.value) return z.color;
  }
  return zones[zones.length - 1]?.color ?? BLUE;
}

function buildSegmentSeries(points: XY[], zones: Zone[]): ApexSeries[] {
  if (!points.length) return [];

  const segments: Array<{ color: string; data: XY[] }> = [];

  let currColor = zoneColorForY(points[0].y, zones);
  let currSeg: XY[] = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    const c = zoneColorForY(p.y, zones);

    if (c === currColor) {
      currSeg.push(p);
      continue;
    }

    segments.push({ color: currColor, data: currSeg });

    // keep continuity
    currColor = c;
    currSeg = [points[i - 1], p];
  }

  segments.push({ color: currColor, data: currSeg });

  return segments.map((s, idx) => ({
    name: `My Data (${idx + 1})`,
    data: s.data,
    _color: s.color,
  }));
}

const MultiZoneChart: React.FC<Props> = ({
  data,
  height,
  width,
  metricInfoDict,
  show_linear_regression = false,
}) => {
  const session = useSelector(selectors.activeSession);

  const metricsLimits: MetricLimits[] = session.metrics_limits ?? [];
  const selectedMetricLimits = useMemo(
    () => metricsLimits.find(m => m.metric_type === metricInfoDict.limit_index),
    [metricsLimits, metricInfoDict.limit_index]
  );

  const isRealTime = session.scenario_type === REAL_TIME_SCENARIO;
  const isDateTimeX = isRealTime || session.show_graph_as_rt;

  const formatter = (val: number | undefined) =>
    metricInputSettings[metricInfoDict.limit_index].player_graph_formatter(val);

  const processedPoints = useMemo((): XY[] => {
    if (!data?.length) return [];

    if (isRealTime) {
      return data
        .map(p => ({ x: Date.parse(p.x as string), y: p.y }))
        .filter(p => Number.isFinite(p.x));
    }

    return data
      .map(p => ({
        x: typeof p.x === 'string' ? Date.parse(p.x) : p.x,
        y: p.y,
      }))
      .filter(p => Number.isFinite(p.x));
  }, [data, isRealTime]);

  const regressionSeries = useMemo((): XY[] => {
    if (!processedPoints.length) return [];

    if (isDateTimeX) {
      const timestamps = processedPoints.map(p => p.x);
      const minTimestamp = Math.min(...timestamps);

      const prepData: [number, number][] = processedPoints.map(p => [
        (p.x - minTimestamp) / ONE_DAY,
        p.y,
      ]);

      const reg = regression.linear(prepData);
      return reg.points.map(([normX, y]) => ({
        x: normX * ONE_DAY + minTimestamp,
        y,
      }));
    }

    // Regress on the points' ACTUAL x (identical to idx*frequency+1 on fixed sessions, where the
    // caller built x exactly that way; correct on predetermined, where x is the real Day N).
    const prepData: [number, number][] = processedPoints.map(p => [p.x, p.y]);

    const reg = regression.linear(prepData);
    return reg.points.map(([x, y]) => ({ x, y }));
  }, [processedPoints, isDateTimeX]);

  const zones = useMemo(
    () => computeZones(selectedMetricLimits),
    [selectedMetricLimits]
  );

  const zonedSeries = useMemo((): ApexSeries[] => {
    const pts = [...processedPoints].sort((a, b) => a.x - b.x);
    return buildSegmentSeries(pts, zones);
  }, [processedPoints, zones]);

  const series = useMemo((): ApexSeries[] => {
    const s: ApexSeries[] = [...zonedSeries];

    if (show_linear_regression && regressionSeries.length) {
      s.push({
        name: 'Linear Regression',
        data: regressionSeries,
        _color: REGRESSION_COLOR,
      });
    }

    return s;
  }, [zonedSeries, show_linear_regression, regressionSeries]);

  // ✅ THIS is what makes red actually appear reliably:
  // colors[] aligns 1:1 with series[] order (segments then regression).
  const colors = useMemo(() => series.map(s => s._color ?? BLUE), [series]);

  const options = useMemo((): ApexOptions => {
    const hasRegression = show_linear_regression && regressionSeries.length > 0;

    return {
      chart: {
        type: 'line',
        height,
        width,
        zoom: { type: 'x', enabled: true },
        toolbar: {
          show: true,
          tools: {
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
            download: false,
          },
        },
      },
      title: { text: metricInfoDict.name },
      legend: { show: false },
      colors,
      stroke: {
        curve: 'smooth',
        width: series.map((_, idx) =>
          hasRegression && idx === series.length - 1 ? 1 : 4
        ),
        dashArray: series.map((_, idx) =>
          hasRegression && idx === series.length - 1 ? 6 : 0
        ),
      },
      markers: { size: 0 },

      xaxis: isDateTimeX
        ? {
            type: 'datetime',
            labels: {
              formatter: (value: string) => formatDate(Number(value), 'axis'),
            },
          }
        : {
            type: 'numeric',
            labels: {
              formatter: (value: string) => formatAxisNumber(Number(value), 0), // no decimals, like example
            },
          },

      yaxis: {
        title: { text: undefined },
        labels: {
          // Volatility is a fraction (0.01 == 1%); render its y-axis as a
          // percentage so the axis matches the tooltip, using the same per-metric
          // formatter the tooltip already uses. Every other metric keeps the
          // generic clean numeric formatter (VaR millions, ratios, …) unchanged.
          formatter: (value: number) =>
            metricInfoDict.limit_index === LIMIT_METRIC_VOLATILITY
              ? formatter(value)
              : formatAxisNumber(value, 2), // clean like example
        },
      },

      tooltip: {
        shared: false,
        intersect: false,
        x: {
          formatter: (val: number) =>
            isDateTimeX ? formatDate(val, 'tooltip') : `Day ${val}`,
        },
        y: {
          formatter: (val: number) => formatter(val),
          title: { formatter: () => `${metricInfoDict.name}: ` },
        },
      },

      grid: {
        padding: { left: 8, right: 8 }, // helps labels look more like your example
      },
    };
  }, [
    height,
    width,
    metricInfoDict.name,
    isDateTimeX,
    formatter,
    colors,
    series,
    show_linear_regression,
    regressionSeries,
  ]);

  // Apex ignores unknown fields on series objects, but to be safe we can strip _color:
  const seriesForChart = useMemo(
    () => series.map(({ _color, ...rest }) => rest),
    [series]
  );

  if (!selectedMetricLimits) return <div />;

  return (
    <Chart
      options={options}
      series={seriesForChart as any}
      type="line"
      height={height}
      width={width}
    />
  );
};

export default MultiZoneChart;
